'use client';

import { cn } from '@/lib/utils';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { CopyPlus, File, LoaderCircle, Plus, Trash } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';
import { File as FileType } from '@/type/chat';

const Attach = ({
  fileIds,
  setFileIds,
  showText,
  files = [],
  setFiles = () => {},
}: {
  fileIds?: string[];
  setFileIds?: (fileIds: string[]) => void;
  showText?: boolean;
  files?: FileType[];
  setFiles?: (files: FileType[]) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<any>(null);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const data = new FormData();
    const token = localStorage.getItem('apiAccessToken');

    for (let i = 0; i < e.target.files!.length; i++) {
      data.append('files', e.target.files![i]);
    }

    const embeddingModelProvider = localStorage.getItem('embeddingModelProvider');
    const embeddingModel = localStorage.getItem('embeddingModel');

    data.append('embedding_model_provider', embeddingModelProvider!);
    data.append('embedding_model', embeddingModel!);
    try {
      const res = await fetch(`/api/uploads`, {
        method: 'POST',
        body: data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const resData = await res.json();
      console.log('resData', resData);
      setFiles([...files, ...resData.files]);
      setFileIds?.([...(fileIds || []), ...resData.files.map((file: any) => file.fileId)]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return loading ? (
    <div className="flex flex-row items-center justify-between space-x-1">
      <LoaderCircle size={18} className="text-sky-400 animate-spin" />
      <p className="text-sky-400 inline whitespace-nowrap text-xs font-medium">Uploading..</p>
    </div>
  ) : files.length > 0 ? (
    <Popover className="relative w-full max-w-60 md:max-w-md lg:max-w-lg">
      <PopoverButton
        type="button"
        className="flex flex-row items-center justify-between space-x-1 p-2 rounded-lg text-black/50 hover:bg-[#E9E9E9] dark:dark:hover:bg-[#111111] dark:text-white/50 active:scale-95 transition duration-200 bg-[#2B2D30] hover:text-black dark:hover:text-white"
      >
        {files.length > 1 && (
          <>
            <File size={19} className="text-sky-400" />
            <p className="text-sky-400 inline whitespace-nowrap text-xs font-medium">
              {files.length} files
            </p>
          </>
        )}

        {files.length === 1 && (
          <>
            <File size={18} className="text-sky-400" />
            <p className="text-sky-400 text-xs font-medium">
              {files[0].fileName.length > 10
                ? files[0].fileName.replace(/\.\w+$/, '').substring(0, 3) +
                  '...' +
                  files[0].fileExtension
                : files[0].fileName}
            </p>
          </>
        )}
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel anchor="bottom" className="absolute z-40 w-64 md:w-[350px] right-0">
          <div className="bg-light-primary bg-dark-primary border rounded-xl border-light-200 dark:border-[#383A3B] w-full max-h-[200px] md:max-h-none overflow-y-auto flex flex-col">
            <div className="flex flex-row items-center justify-between p-3">
              <h4 className="text-text-primary font-medium text-sm">Attached files</h4>
              <div className="flex flex-row items-center space-x-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="flex flex-row items-center space-x-1 text-text-primary hover:text-black dark:hover:text-white transition duration-200"
                >
                  <input
                    type="file"
                    onChange={handleChange}
                    ref={fileInputRef}
                    accept=".pdf,.docx,.txt"
                    multiple
                    hidden
                  />
                  <Plus size={18} />
                  <p className="text-xs">Add</p>
                </button>
                <button
                  onClick={() => {
                    setFiles([]);
                    setFileIds?.([]);
                  }}
                  className="flex flex-row items-center space-x-1 text-text-primary hover:text-black dark:hover:text-white transition duration-200"
                >
                  <Trash size={14} />
                  <p className="text-xs">Clear</p>
                </button>
              </div>
            </div>
            <div className="h-[0.5px] bg-[#383A3B]" />
            <div className="flex flex-col items-center py-2">
              {files.map((file, i) => (
                <div
                  key={i}
                  className="flex flex-row items-center justify-start w-full space-x-3 px-3 py-2"
                >
                  <div className="bg-dark-100 flex items-center justify-center w-10 h-10 rounded-md">
                    <File size={16} className="text-white/70" />
                  </div>
                  <p className="flex-1 text-text-primary text-sm truncate">
                    {file.fileName.length > 25
                      ? file.fileName.replace(/\.\w+$/, '').substring(0, 25) +
                        '...' +
                        file.fileExtension
                      : file.fileName}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  ) : (
    <button
      type="button"
      onClick={() => fileInputRef.current.click()}
      className={cn(
        'flex flex-row items-center rounded-lg p-2 hover:bg-[#2B2D30] dark:text-white/50 transition duration-200 hover:text-black dark:hover:text-white',
      )}
    >
      <input
        type="file"
        onChange={handleChange}
        ref={fileInputRef}
        accept=".pdf,.docx,.txt"
        multiple
        hidden
      />
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.3333 4.99999L6.32161 12.155C6.00898 12.4676 5.83335 12.8916 5.83335 13.3337C5.83335 13.7759 6.00898 14.1999 6.32161 14.5125C6.63423 14.8251 7.05824 15.0008 7.50036 15.0008C7.94247 15.0008 8.36648 14.8251 8.67911 14.5125L15.6908 7.3575C16.3159 6.73236 16.6671 5.88449 16.6671 5.00041C16.6671 4.11633 16.3159 3.26847 15.6908 2.64333C15.0656 2.01819 14.2178 1.66699 13.3337 1.66699C12.4496 1.66699 11.6017 2.01819 10.9766 2.64333L3.99411 9.76916C3.52361 10.2321 3.14941 10.7835 2.89311 11.3918C2.6368 12 2.50346 12.653 2.50077 13.313C2.49809 13.973 2.62611 14.6271 2.87745 15.2374C3.12879 15.8477 3.49848 16.4022 3.9652 16.8689C4.43191 17.3356 4.98642 17.7053 5.59672 17.9567C6.20703 18.208 6.86106 18.336 7.52109 18.3333C8.18112 18.3306 8.83409 18.1973 9.44233 17.941C10.0506 17.6847 10.602 17.3105 11.0649 16.84L18.0474 9.71416"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* <SearchFileIcon /> */}
      {/* <CopyPlus size={showText ? 18 : undefined} /> */}
      {showText && <p className="text-xs font-medium pl-px">Attach</p>}
    </button>
  );
};

export default Attach;
